import {
  getOrgDirectory,
  type OrgMemberView,
  type OrgMemberWorkspaceRef,
  type RegistryGroupRef,
} from "@/api";
import type { UserRole } from "@/app/userRole";
import {
  DataTableSource,
  type DataTablePageReq,
  type DataTablePageResp,
  type DataTableSourceOptions,
} from "@carbon6/c6-frontend-design";
import { OrgMemberTableRow } from "./OrgMemberTableRow";

export type RegisteredFilter = "all" | "registered" | "unregistered";

type OrgMemberTableSourceOptions = DataTableSourceOptions & {
  // URL 에서 읽은 첫 조건. 첫 로드에 바로 적용한다. 그래서 initialize 뒤 setter 호출이 no-op 이다.
  initialSearch?: string;
  initialRegisteredFilter?: RegisteredFilter;
};

// 검색은 대소문자를 구분하지 않는 contains 다. 같은 조건의 재페이징을 막으려고 여기서 먼저
// 소문자로 바꾼다.
function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

// org 디렉터리는 페이지네이션 없는 단일 GET 이다. 그래서 전체를 한 번 받아 메모리에 두고
// 검색·가입 필터를 클라이언트에서 적용한다. 매핑/등급 변이는 캐시 멤버를 갱신한다. 그래서 전체를
// 다시 요청하지 않는다.
export class OrgMemberTableSource extends DataTableSource<OrgMemberTableRow> {
  private allMembers: OrgMemberView[] | null = null;
  private search = "";
  private registeredFilter: RegisteredFilter = "all";

  constructor({
    initialSearch = "",
    initialRegisteredFilter = "all",
    ...options
  }: OrgMemberTableSourceOptions = {}) {
    super(options);
    this.search = normalizeSearch(initialSearch);
    this.registeredFilter = initialRegisteredFilter;
  }

  setSearch(value: string): Promise<void> {
    const next = normalizeSearch(value);
    if (next === this.search) {
      return Promise.resolve();
    }
    this.search = next;
    return this.goToPage(1);
  }

  setRegisteredFilter(value: RegisteredFilter): Promise<void> {
    if (value === this.registeredFilter) {
      return Promise.resolve();
    }
    this.registeredFilter = value;
    return this.goToPage(1);
  }

  // 서버에서 디렉터리를 다시 받는다(새로고침 버튼, 매핑 변경 후 정합 재조회).
  reload(): Promise<void> {
    this.allMembers = null;
    return this.refresh();
  }

  // 등급 변경 결과를 캐시 멤버(가입자)에 반영한다. 전체를 다시 요청하지 않고 다시 페이징한다.
  applyTier(memberUuid: string, tier: UserRole): Promise<void> {
    const member = this.allMembers?.find((entry) => entry.member_uuid === memberUuid);
    if (member) {
      member.tier = tier;
    }
    return this.goToPage(1);
  }

  // 그룹 매핑 결과를 캐시 멤버에 반영한다. 모달이 변이마다 전달하는 다음 목록이 곧 정답이다.
  // 등급과 다르게 github_user_id 로 찾는다. 미가입 멤버도 매핑 대상이고, 이 값은 login 이
  // 바뀌어도 변하지 않는다.
  applyRegistryGroups(
    githubUserId: number,
    registryGroups: readonly RegistryGroupRef[],
  ): Promise<void> {
    const member = this.findMember(githubUserId);
    if (member) {
      member.registry_groups = [...registryGroups];
    }
    return this.goToPage(1);
  }

  // workspace 매핑 결과를 캐시 멤버에 반영한다. 가입자만 대상이다. 그래서 미가입 멤버에는
  // 호출하지 않는다.
  applyWorkspaces(
    githubUserId: number,
    workspaces: readonly OrgMemberWorkspaceRef[],
  ): Promise<void> {
    const member = this.findMember(githubUserId);
    if (member) {
      member.training_workspaces = [...workspaces];
    }
    return this.goToPage(1);
  }

  private findMember(githubUserId: number): OrgMemberView | undefined {
    return this.allMembers?.find((entry) => entry.github_user_id === githubUserId);
  }

  protected override async onPage(
    req: DataTablePageReq,
  ): Promise<DataTablePageResp<OrgMemberTableRow>> {
    if (this.allMembers === null) {
      const directory = await getOrgDirectory();
      this.allMembers = [...directory.members];
    }
    const filtered = this.applyFilters(this.allMembers);
    return {
      rows: filtered.map((member) => new OrgMemberTableRow(member)),
      total: filtered.length,
      page: 1,
      size: Math.max(req.size, filtered.length, 1),
    };
  }

  // 검색은 login·GitHub 팀·Registry 팀명을 본다. 가입 필터는 registered 플래그를 본다.
  private applyFilters(members: readonly OrgMemberView[]): OrgMemberView[] {
    return members.filter((member) => {
      if (this.registeredFilter === "registered" && !member.registered) {
        return false;
      }
      if (this.registeredFilter === "unregistered" && member.registered) {
        return false;
      }
      if (this.search === "") {
        return true;
      }
      const haystack = [
        member.github_login,
        ...member.github_teams,
        ...member.registry_groups.map((ref) => ref.name),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(this.search);
    });
  }
}
